import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiFileText,
  FiRefreshCw,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCalendar,
  FiUsers,
  FiSend,
  FiPaperclip
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../api';
import AssignmentModal from './AssignmentModal';
import AssignmentSubmissionsModal from './AssignmentSubmissionsModal';

const AssignmentManager = ({ classItem, subject, labBatch, facultyId, refreshKey, onRefresh }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null); // Local loading flag for delete transactions
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [notification, setNotification] = useState(null);
  const itemsPerPage = 10;
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchAssignments = useCallback(async () => {
    if (!classItem?.classId || !subject?.subjectId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (labBatch?.batchId) params.append('labBatchId', labBatch.batchId);
      
      const url = `/api/faculty/lms/assignments/${classItem.classId}/${subject.subjectId}?${params}`;
      const response = await api.get(url);
      
      if (response.data?.success) {
        setAssignments(response.data.data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      showNotification('Failed to load assignments', 'error');
    } finally {
      setLoading(false);
    }
  }, [classItem, subject, labBatch]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments, refreshKey]);

  const handleDelete = async (assignmentId) => {
    setDeletingId(assignmentId);
    try {
      await api.delete(`/api/faculty/lms/assignment/${assignmentId}`);
      setShowDeleteConfirm(null);
      fetchAssignments();
      if (onRefresh) onRefresh();
      showNotification('Assignment removed successfully', 'success');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      showNotification('Failed to remove assignment', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePublish = async (assignment) => {
    if (!assignment.visibleToStudents) {
      showNotification('Please make this assignment visible to students first before publishing it.', 'error');
      return;
    }

    const nextPublishedState = !assignment.isPublished;
    try {
      await api.put(`/api/faculty/lms/assignment/${assignment._id}/publish`, { isPublished: nextPublishedState });
      fetchAssignments();
      if (onRefresh) onRefresh();
      showNotification(
        nextPublishedState ? 'Assignment published successfully' : 'Assignment status changed back to draft',
        'success'
      );
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Failed to change assignment status', 'error');
    }
  };

  const handleViewSubmissions = (assignment) => {
    setSelectedAssignment(assignment);
    setShowSubmissionsModal(true);
  };

  const handleOpenModal = (assignment = null) => {
    setEditingAssignment(assignment);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAssignment(null);
  };

  const handleModalSuccess = () => {
    fetchAssignments();
    if (onRefresh) onRefresh();
  };

  const getPublicFileUrl = (fileUrl) => {
    if (!fileUrl) return '#';
    if (fileUrl.startsWith('http')) return fileUrl;
    
    let url = fileUrl;
    if (url.includes('/api/faculty/lms/file/')) {
      url = url.replace('/api/faculty/lms/file/', '/api/files/lms/');
    }
    
    if (!url.startsWith('http')) {
      url = `${API_BASE_URL}${url}`;
    }
    
    return url;
  };

  // Helper logic to derive the true operational status dynamically
  const getDerivedStatus = (assignment) => {
    if (!assignment.isPublished) return 'draft';
    
    const now = new Date();
    const [hours, minutes] = (assignment.dueTime || '23:59').split(':');
    const targetDueDate = new Date(assignment.dueDate);
    targetDueDate.setHours(parseInt(hours), parseInt(minutes), 59, 999);

    if (now > targetDueDate) return 'closed';
    return 'open';
  };

  const getStatusBadge = (derivedStatus) => {
    const configs = {
      draft: { 
        color: 'bg-slate-100 text-slate-700 border-slate-200', 
        label: 'Draft',
        icon: FiEdit2,
        description: 'Hidden from student dashboards'
      },
      open: { 
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200', 
        label: 'Open',
        icon: FiCheckCircle,
        description: 'Visible to students and open for work submissions'
      },
      closed: { 
        color: 'bg-rose-50 text-rose-700 border-rose-200', 
        label: 'Closed',
        icon: FiClock,
        description: 'Submission final deadline has passed'
      }
    };
    return configs[derivedStatus] || configs.draft;
  };

  const filteredAssignments = assignments.filter(a => {
    const derivedStatus = getDerivedStatus(a);
    const matchesSearch = a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          a.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    return matchesSearch && derivedStatus === statusFilter;
  });

  const paginatedItems = filteredAssignments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage);

  const drawerVariants = {
    hidden: { 
      y: window.innerWidth < 640 ? '100%' : 15, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0 
    },
    visible: { 
      y: 0, 
      scale: 1, 
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 350 }
    },
    exit: { 
      y: window.innerWidth < 640 ? '100%' : 10, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0,
      transition: { duration: 0.15 }
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Toast Alert System Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border flex items-start gap-3 max-w-sm text-xs font-semibold backdrop-blur-md ${
              notification.type === 'success' 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}
          >
            {notification.type === 'success' 
              ? <FiCheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              : <FiAlertCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
            }
            <span className="flex-1 leading-normal">{notification.message}</span>
            <button onClick={() => setNotification(null)} className="text-slate-400 hover:text-slate-600">
              <FiX className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metrics Dashboard Row */}
      {assignments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm text-center">
            <p className="text-xl font-bold text-slate-700">
              {assignments.filter(a => getDerivedStatus(a) === 'draft').length}
            </p>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Drafts</p>
          </div>
          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm text-center">
            <p className="text-xl font-bold text-emerald-600">
              {assignments.filter(a => getDerivedStatus(a) === 'open').length}
            </p>
            <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-wider mt-0.5">Open</p>
          </div>
          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm text-center">
            <p className="text-xl font-bold text-rose-600">
              {assignments.filter(a => getDerivedStatus(a) === 'closed').length}
            </p>
            <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider mt-0.5">Closed</p>
          </div>
        </div>
      )}

      {/* Primary Management Panel Header Toolbar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-800">Assignments Manager</h2>
            <p className="text-xs text-slate-400 font-medium">Create, publish, and evaluate student class assignment submissions</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-stretch sm:justify-end">
            <button
              onClick={fetchAssignments}
              className="p-2 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-all"
              title="Sync Data"
              disabled={loading}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => handleOpenModal(null)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs transition-all shadow-sm shadow-indigo-100"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Add Assignment
            </button>
          </div>
        </div>

        {/* Filter Selection and Search Inputs Bar */}
        <div className="flex flex-col md:flex-row gap-3 pt-2 border-t border-slate-100">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by assignment title or description keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs text-slate-700 transition-all placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center bg-slate-100/80 p-1 rounded-lg border border-slate-200/40 overflow-x-auto no-scrollbar">
            {['all', 'draft', 'open', 'closed'].map(status => (
              <button
                key={status}
                onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all uppercase tracking-wide text-[10px] whitespace-nowrap ${
                  statusFilter === status
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {status === 'all' ? 'Show All' : status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Table Content Display */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200/80 shadow-sm space-y-3">
          <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-600 border-t-transparent"></div>
          <p className="text-xs text-slate-400 font-medium">Getting latest documents...</p>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200/80 shadow-sm px-4">
          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-100 text-slate-400">
            <FiFileText className="w-5 h-5" />
          </div>
          <p className="text-sm font-semibold text-slate-700">No records found</p>
          <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">Try altering your search or click "Add Assignment" to create a new record entry.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
          {/* Desktop Table Presentation Grid */}
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">Assignment Details</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-44">Timeline & Info</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-32 text-center">Status</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-40 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedItems.map((assignment) => {
                  const derivedStatus = getDerivedStatus(assignment);
                  const statusConfig = getStatusBadge(derivedStatus);
                  const StatusIcon = statusConfig.icon;
                  const totalSubmissions = assignment.submissions?.total || 0;
                  const gradedCount = assignment.submissions?.graded || 0;

                  return (
                    <tr 
                      key={assignment._id}
                      className="hover:bg-slate-50/60 transition-colors cursor-pointer group"
                      onClick={() => handleViewSubmissions(assignment)}
                    >
                      <td className="p-4 max-w-sm">
                        <div className="space-y-1">
                          <h3 className="text-xs font-bold text-slate-700 group-hover:text-indigo-600 transition-colors line-clamp-1">
                            {assignment.title}
                          </h3>
                          <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed font-normal">{assignment.description}</p>
                          
                          {/* Reference Attachments Section */}
                          {assignment.attachments && assignment.attachments.length > 0 && (
                            <div className="flex items-center gap-1.5 pt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                              <FiPaperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              <div className="flex items-center gap-2">
                                {assignment.attachments.slice(0, 2).map((file, idx) => (
                                  <a
                                    key={idx}
                                    href={getPublicFileUrl(file.fileUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline max-w-[110px] truncate font-medium bg-slate-100 px-1.5 py-0.2 border border-slate-200/60 rounded"
                                    title={file.name}
                                  >
                                    {file.name}
                                  </a>
                                ))}
                                {assignment.attachments.length > 2 && (
                                  <span className="text-[10px] text-slate-400 font-medium">+{assignment.attachments.length - 2} files</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="p-4 text-slate-600 space-y-1 text-[11px] font-medium">
                        <div className="flex items-center gap-1 text-slate-500">
                          <FiCalendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>Due: {new Date(assignment.dueDate).toLocaleDateString()} at {assignment.dueTime}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span>Max Marks: <strong className="text-slate-700">{assignment.maxMarks}</strong></span>
                          <span className="flex items-center gap-0.5">
                            <FiUsers className="w-3 h-3 text-slate-400" />
                            {totalSubmissions} turned in
                          </span>
                        </div>
                        {gradedCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600 bg-emerald-50 px-1.5 py-0.2 border border-emerald-100 rounded text-[10px]">
                            <FiCheckCircle className="w-3 h-3" />
                            {gradedCount} evaluated
                          </span>
                        )}
                      </td>

                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold border tracking-wide uppercase ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3 inline mr-1" />
                            {statusConfig.label}
                          </span>
                        </div>
                      </td>

                      <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleViewSubmissions(assignment)}
                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-lg transition-all"
                            title="Submissions List"
                          >
                            <FiUsers className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(assignment)}
                            className="w-7 h-7 flex items-center justify-center text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-lg transition-all"
                            title="Edit Assignment"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handlePublish(assignment)}
                            className={`w-7 h-7 flex items-center justify-center border border-transparent rounded-lg transition-all ${
                              assignment.isPublished
                                ? 'text-emerald-600 hover:bg-emerald-50 hover:border-emerald-100'
                                : 'text-slate-400 hover:bg-slate-50 hover:border-slate-200'
                            }`}
                            title={assignment.isPublished ? 'Move to Draft' : 'Publish to Students'}
                          >
                            <FiSend className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(assignment._id)}
                            className="w-7 h-7 flex items-center justify-center text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all"
                            title="Delete Assignment"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Responsive Mobile Card Presentation */}
          <div className="block sm:hidden divide-y divide-slate-100 bg-white">
            {paginatedItems.map((assignment) => {
              const derivedStatus = getDerivedStatus(assignment);
              const statusConfig = getStatusBadge(derivedStatus);
              const totalSubmissions = assignment.submissions?.total || 0;
              return (
                <div
                  key={assignment._id}
                  className="p-4 space-y-2.5 active:bg-slate-50/70 transition-colors"
                  onClick={() => handleViewSubmissions(assignment)}
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold border uppercase tracking-wide ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <h3 className="text-xs font-bold text-slate-800 line-clamp-2 leading-snug">{assignment.title}</h3>
                  </div>

                  <div className="text-[10px] text-slate-500 font-medium space-y-0.5">
                    <p className="flex items-center gap-1">
                      <FiCalendar className="w-3 h-3 text-slate-400" /> Due: {new Date(assignment.dueDate).toLocaleDateString()} • {assignment.dueTime}
                    </p>
                    <div className="flex gap-3 text-slate-400 font-semibold">
                      <span>Max Marks: <strong className="text-slate-600">{assignment.maxMarks}</strong></span>
                      <span>{totalSubmissions} turned in</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleViewSubmissions(assignment)}
                      className="w-7 h-7 flex items-center justify-center text-slate-500 bg-slate-50 border border-slate-200/60 rounded-lg"
                    >
                      <FiUsers className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleOpenModal(assignment)}
                      className="w-7 h-7 flex items-center justify-center text-blue-600 bg-blue-50 border border-blue-100 rounded-lg"
                    >
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handlePublish(assignment)}
                      className={`w-7 h-7 flex items-center justify-center border rounded-lg ${
                        assignment.isPublished
                          ? 'text-emerald-600 bg-emerald-50 border-emerald-100'
                          : 'text-slate-400 bg-slate-50 border-slate-200'
                      }`}
                    >
                      <FiSend className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(assignment._id)}
                      className="w-7 h-7 flex items-center justify-center text-rose-600 bg-rose-50 border border-rose-100 rounded-lg"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors shadow-sm"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-semibold text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors shadow-sm"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Box Overlay */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-t-2xl sm:rounded-2xl max-w-sm w-full p-5 border border-slate-100 shadow-xl"
            >
              <div className="w-full flex justify-center py-2 sm:hidden bg-white">
                <div className="w-12 h-1 bg-slate-200 rounded-full" />
              </div>
              <div className="flex items-center gap-3 text-rose-600 mb-3 mt-1 sm:mt-0">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500 border border-rose-100">
                  <FiAlertCircle className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">Delete Assignment</h3>
              </div>
              <p className="text-xs text-slate-500 leading-normal mb-5">
                Are you sure you want to remove this assignment? This process cannot be undone.
              </p>
              <div className="flex gap-2.5 justify-stretch sm:justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 sm:flex-none px-3.5 py-2 text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold rounded-lg shadow-sm transition-colors"
                  disabled={deletingId !== null}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg shadow-sm shadow-rose-100 transition-colors disabled:opacity-50"
                  disabled={deletingId !== null}
                >
                  {deletingId === showDeleteConfirm ? (
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  {deletingId === showDeleteConfirm ? 'Removing...' : 'Delete Assignment'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Assignment Modal Form popup */}
      <AssignmentModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        classItem={classItem}
        subject={subject}
        labBatch={labBatch}
        facultyId={facultyId}
        editingAssignment={editingAssignment}
      />

      {/* Submissions List Panel Overlay Drawer */}
      <AssignmentSubmissionsModal
        isOpen={showSubmissionsModal}
        onClose={() => {
          setShowSubmissionsModal(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        classItem={classItem}
        subject={subject}
        facultyId={facultyId}
      />
    </div>
  );
};

export default AssignmentManager;